import { ExternalLink } from 'lucide-react'

import type { DataReviewDecisionInput, DataReviewItem } from '@/services/dataReview'
import { CandidateRow, Section } from './DatapointPanel'
import { dossierTabFor, kindMeta } from './dataReviewFormat'
import { DecisionBar } from './DecisionBar'

type Answer = Omit<DataReviewDecisionInput, 'itemId' | 'note' | 'overrideHumanDecision'>

/**
 * Something only a person can settle on a promoted deal.
 *
 * Two kinds are answered here: the package names the counterparty more than
 * one way and the parser declined to choose (pick one, type one, or say none is
 * available), or it names nobody at all. The rest of what the reconciliation
 * sweep lists concerns a value ON THE DEAL a person set, a workbook only the
 * Underwriting page can map, or a role only its owner can assign — a review
 * decision must never overwrite those, so the row offers no verb and says where
 * the question is answered.
 */
export function NeedsPersonPanel({
  item, busy, value, onValue, reason, onReason, onDecide, onOpenDeal,
}: {
  item: DataReviewItem
  busy: boolean
  value: string
  onValue: (value: string) => void
  reason: string
  onReason: (reason: string) => void
  onDecide: (answer: Answer) => void
  /** Closes this review and opens the deal's dossier on the given tab. */
  onOpenDeal: (loanId: string, tab: 'underwriting' | 'overview') => void
}) {
  const meta = kindMeta(item.kind)
  const elsewhere = item.actionElsewhere
  const options = item.options ?? []
  const offers = (verb: DataReviewItem['actions'][number]) => item.actions.includes(verb)
  const asOf = item.detail?.asOf

  return (
    <div className="space-y-3">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] font-medium text-stone-100">{item.label}</span>
          <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[8px] font-medium uppercase tracking-wide ${meta.cls}`}>
            {meta.label}
          </span>
        </div>
        <p className="mt-1 text-[10.5px] leading-relaxed text-stone-400">{item.question}</p>
      </div>

      {elsewhere && (
        <div className="rounded border border-stone-700 bg-stone-900/50 px-3 py-2.5">
          <div className="text-[9px] uppercase tracking-wide text-stone-600">Answered elsewhere</div>
          <p className="mt-1 text-[11px] leading-relaxed text-stone-300">
            A review decision cannot answer this one — it is answered in {elsewhere.where}.
          </p>
          {elsewhere.workbook && (
            <p className="mt-1 font-mono text-[10px] text-stone-500">Workbook: {elsewhere.workbook}</p>
          )}
          {item.value != null && item.value !== '' && (
            <p className="mt-1 text-[10.5px] text-stone-400">
              The deal holds <span className="font-mono text-stone-200">{String(item.value)}</span>
              {item.decidedBy ? <> — set by {item.decidedBy}</> : null}.
            </p>
          )}
          {item.loanId && (
            <button
              type="button"
              onClick={() => onOpenDeal(item.loanId!, dossierTabFor(elsewhere.where))}
              className="mt-2 inline-flex items-center gap-1 rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[10.5px] font-medium text-amber-300 transition hover:border-amber-400 hover:bg-amber-400/20"
            >
              <ExternalLink size={10} />
              {dossierTabFor(elsewhere.where) === 'underwriting' ? 'Open the deal’s Underwriting page' : 'Open the deal file'}
            </button>
          )}
          {asOf && (
            <p className="mt-2 text-[9px] text-stone-600">
              As found by the reconciliation sweep of {new Date(asOf).toLocaleString()}.
            </p>
          )}
        </div>
      )}

      {!elsewhere && options.length > 0 && (
        <Section title="What the package says">
          <div className="space-y-1.5">
            {options.map((option, i) => (
              <CandidateRow
                key={i}
                value={option.value}
                why={option.source ?? undefined}
                relation={option.relation ?? undefined}
                provenance={option.provenance}
                disabled={busy}
                onUse={() => onDecide({ verb: 'correct', value: option.value })}
              />
            ))}
          </div>
        </Section>
      )}

      {!elsewhere && (
        <div className="sticky bottom-0 -mx-4 border-t border-stone-800 bg-stone-950/95 px-4 pb-1 pt-2.5 backdrop-blur">
          <DecisionBar
            busy={busy}
            correct={offers('correct') ? {
              value,
              onValue,
              placeholder: 'Enter the counterparty',
              onUse: (typed) => onDecide({ verb: 'correct', value: typed }),
            } : undefined}
            unavailable={offers('unavailable') ? {
              reason,
              onReason,
              onUse: (why) => onDecide({ verb: 'unavailable', reason: why }),
            } : undefined}
          />
          <p className="mt-1 text-[9px] text-stone-600">
            Recorded on the checklist and the deal, against your name. A value a person set on the
            deal itself is never overwritten from here.
          </p>
        </div>
      )}
    </div>
  )
}
